import { useState } from "react";
import { Boxes, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

function effStock(l) {
  if (l.variants?.length) {
    if (l.variants.every((v) => v.stock == null)) return null;
    return l.variants.reduce((s, v) => s + (v.stock ?? 0), 0);
  }
  return l.inventory;
}

export function StockEditor({ listing, onUpdated }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [inv, setInv] = useState(listing.inventory ?? "");
  const [vstocks, setVstocks] = useState(() => Object.fromEntries((listing.variants || []).map((v) => [v.id, v.stock ?? ""])));

  const total = effStock(listing);
  const hasVariants = (listing.variants?.length || 0) > 0;
  const label = total == null ? "Unlimited" : total <= 0 ? "Sold out" : `${total} in stock`;
  const cls = total != null && total <= 0 ? "text-blaze" : total != null && total <= 5 ? "text-blaze-dark" : "text-[#2f7d4f]";

  const save = async () => {
    setBusy(true);
    try {
      const body = hasVariants
        ? { variant_stocks: Object.fromEntries(Object.entries(vstocks).map(([k, v]) => [k, Number(v) || 0])) }
        : { inventory: inv === "" ? 0 : Number(inv) };
      await api.updateStock(listing.id, body);
      toast.success("Stock updated");
      setOpen(false);
      onUpdated?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update stock"); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-2 pt-2 border-t border-saddle/10" data-testid={`stock-editor-${listing.id}`}>
      <div className="flex items-center justify-between">
        <span className={`text-xs font-semibold inline-flex items-center gap-1.5 ${cls}`}><Boxes size={13} /> {label}</span>
        <button onClick={() => setOpen((o) => !o)} className="text-[11px] font-semibold text-forest hover:text-blaze" data-testid={`stock-edit-toggle-${listing.id}`}>{open ? "Cancel" : "Edit stock"}</button>
      </div>
      {open && (
        <div className="mt-2 space-y-1.5" data-testid={`stock-fields-${listing.id}`}>
          {hasVariants ? (
            listing.variants.map((v) => (
              <div key={v.id} className="flex items-center gap-2">
                <span className="flex-1 text-xs text-charcoal/70 truncate">{v.label}</span>
                <input type="number" min="0" value={vstocks[v.id]} onChange={(e) => setVstocks((p) => ({ ...p, [v.id]: e.target.value }))} placeholder="∞" className="w-24 border border-saddle/25 px-2 py-1 text-sm outline-none" data-testid={`stock-variant-${v.id}`} />
              </div>
            ))
          ) : (
            <div className="flex items-center gap-2">
              <span className="flex-1 text-xs text-charcoal/70">Units in stock</span>
              <input type="number" min="0" value={inv} onChange={(e) => setInv(e.target.value)} placeholder="0" className="w-24 border border-saddle/25 px-2 py-1 text-sm outline-none" data-testid={`stock-input-${listing.id}`} />
            </div>
          )}
          <button onClick={save} disabled={busy} className="bg-forest text-bone px-4 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-60" data-testid={`stock-save-${listing.id}`}>
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Save stock
          </button>
        </div>
      )}
    </div>
  );
}
